import React from "react";
import { Link } from "react-router-dom";
import { FiTag, FiX } from "react-icons/fi";

/**
 * LabelTag Component
 * 
 * Props:
 * - name: string (label name)
 * - onClick?: function (optional click handler)
 * 
 * Renders:
 * - A tag containing a heart outline icon (♡) and the label name in gray.
 * - Clicking the tag triggers onClick if provided, otherwise navigates to the tasks page filtered by this label.
 * - Read-only (unless onRemove is provided).
 */
export function LabelTag({ name, onClick, onRemove, color }) {
  const style = color
    ? {
        backgroundColor: `${color}12`, // ~7% opacity
        borderColor: `${color}30`,     // ~18% opacity
        color: color,
      }
    : {
        backgroundColor: "var(--theme-color-canvas-soft)",
        borderColor: "var(--theme-color-mute)",
        color: "var(--theme-color-body-mid)",
      };

  const content = (
    <span
      className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded gap-1 select-none border transition-colors duration-150 hover:opacity-90"
      style={style}
    >
      <FiTag className="w-3 h-3 flex-shrink-0" style={{ color: color || "currentColor" }} />
      <span>{name}</span>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove(name);
          }}
          className="ml-0.5 hover:text-red-500 focus:outline-none border-none outline-none bg-transparent p-0 m-0 flex items-center justify-center w-3 h-3"
        >
          <FiX className="w-2.5 h-2.5" />
        </button>
      )}
    </span>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="focus:outline-none focus:ring-1 focus:ring-gray-300 rounded"
      >
        {content}
      </button>
    );
  }

  return (
    <Link
      to={`/tasks/label/${encodeURIComponent(name)}`}
      className="no-underline focus:outline-none focus:ring-1 focus:ring-gray-300 rounded"
    >
      {content}
    </Link>
  );
}
